import React, { useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import {
  Modal,
  Box,
  Button,
  Typography,
  Tab,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { apiService } from "../../../../../api/apiService";
import BusinessDescription from "./ModalComponent/BusinessDescription";
import KPIBasket from "./ModalComponent/KPIBasket";
import CriticalMatrix from "./ModalComponent/CriticalMatrix";
import FilePreview from "../../../utils/FilePreview/FilePreview";
import ActionButtonsFooter from "../../../utils/ActionButtonsFooter";

interface FileViewPopupProps {
  open: boolean;
  onClose: () => void;
  name: string;
  id: string;
  functionl1?: string;
  functionl2?: string;
  functionl3?: string;
  date: string;
  updatedBy?: any;
  status: string;
  reason_to_approve: string;
  reason_to_revert: string;
  sectionTitle: string;
  apiPath: string;
  statusApiPath: string;
  onSuccess?: () => void;
}

interface SipocModalProps {
  open: boolean;
  onClose: () => void;
  sipocId: string | null;
  functionL1: string;
  functionL2: string;
}

interface TabTexts {
  [key: string]: { reasonText: string; tempText: string };
}

interface TabStates {
  [key: string]: { showInput: boolean };
}

interface ReasonStatus {
  [key: string]: boolean;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const pageNames = [
  "Business Description",
  "Job Hierarchy",
  "KPI Basket",
  "Critical Matrix",
];
const totalTabs = pageNames.length;

const SipocModalAdmin: React.FC<SipocModalProps> = ({
  open,
  onClose,
  sipocId,
  functionL1,
  functionL2,
}) => {
  const management =
    localStorage.getItem("management") || "Default Location Name";
  const apiManagement =
    localStorage.getItem("apiManagement") || "Default Location Name";
  const isManagementMatching = management === apiManagement;

  const { instance } = useMsal();
  const [value, setValue] = useState(
    () => localStorage.getItem("currentTab") || "1"
  );
  const [sipocData, setSipocData] = useState<any | null>(null);
  const [imagePreview, setImagePreview] = useState<any | null>(null);
  //const [loading, setLoading] = useState<boolean>(false);
  const [loadingStates, setLoadingStates] = useState({
    tab1: false,
    tab2: false,
    tab3: false,
    tab4: false,
  });
  const [error, setError] = useState<string | null>(null);

  // State for tab management
  const [tabTexts, setTabTexts] = useState<TabTexts>(() =>
    Object.fromEntries(
      Array.from({ length: totalTabs }, (_, i) => [
        (i + 1).toString(),
        { reasonText: "", tempText: "" },
      ])
    )
  );

  const [tabStates, setTabStates] = useState<TabStates>(() =>
    Object.fromEntries(
      Array.from({ length: totalTabs }, (_, i) => [
        (i + 1).toString(),
        { showInput: false },
      ])
    )
  );

  const [isReasonApproved, setIsReasonApproved] = useState<ReasonStatus>(() =>
    Object.fromEntries(
      Array.from({ length: totalTabs }, (_, i) => [(i + 1).toString(), false])
    )
  );

  const [isReasonRevert, setIsReasonReverted] = useState<ReasonStatus>(() =>
    Object.fromEntries(
      Array.from({ length: totalTabs }, (_, i) => [(i + 1).toString(), false])
    )
  );

  // Action state
  const [clickCount, setClickCount] = useState(0);
  const [isApprove, setIsApprove] = useState(false);
  const [approveHidden, setApproveHidden] = useState(false);
  const [revertHidden, setRevertHidden] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState("#e6ffed");

  // Fetch data functions
  // const fetchBusinessDescription = useCallback(async () => {
  //   try {
  //     const result = await apiService(
  //       `/management/business_description/${sipocId}`,
  //       "GET",
  //       undefined,
  //       undefined,
  //       instance as PublicClientApplication,
  //     );
  //     setSipocData(result.data);
  //   } catch (error) {
  //     console.error("Error fetching data:", error);
  //   }
  // }, [sipocId, instance]);

  const fetchBusinessDescription = useCallback(async () => {
    setLoadingStates((prev) => ({ ...prev, tab1: true }));
    try {
      const result = await apiService(
        `/management/business_description/${sipocId}`,
        "GET",
        undefined,
        undefined,
        instance as PublicClientApplication
      );
      setSipocData(result.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, tab1: false }));
    }
  }, [sipocId, instance]);

  // const fetchJobHierarchyData = useCallback(async () => {
  //   if (!sipocData?.job_hierarchy_data?.doc_path) return;

  //   try {
  //     const downloadResult = await apiService(
  //       `/download?doc_path=${encodeURIComponent(sipocData.job_hierarchy_data.doc_path)}`,
  //       "GET",
  //       undefined,
  //       undefined,
  //       instance as PublicClientApplication,
  //     );
  //     setImagePreview(downloadResult.data);
  //   } catch (error) {
  //     console.error("Error fetching job hierarchy:", error);
  //   }
  // }, [sipocData, instance]);

  const fetchJobHierarchyData = useCallback(async () => {
    if (!sipocData?.job_hierarchy_data?.doc_path) return;
    setLoadingStates((prev) => ({ ...prev, tab2: true }));
    try {
      const downloadResult = await apiService(
        `/download?doc_path=${encodeURIComponent(sipocData.job_hierarchy_data.doc_path)}`,
        "GET",
        undefined,
        undefined,
        instance as PublicClientApplication
      );
      setImagePreview(downloadResult.data);
    } catch (error) {
      console.error("Error fetching job hierarchy:", error);
      setError("Failed to load file.");
    } finally {
      setLoadingStates((prev) => ({ ...prev, tab2: false }));
    }
  }, [sipocData, instance]);

  // Tab navigation
  const resetStateForTab = useCallback(() => {
    setApproveHidden(false);
    setRevertHidden(false);
    setClickCount(0);
    setIsApprove(false);
    setBackgroundColor("#e6ffed");
  }, []);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
    resetStateForTab();
  };

  const navigateTab = useCallback(
    (direction: "next" | "prev") => {
      setValue((prevValue) => {
        const current = parseInt(prevValue);
        const newValue =
          direction === "next"
            ? (current % totalTabs) + 1
            : current === 1
              ? totalTabs
              : current - 1;
        return newValue.toString();
      });
      resetStateForTab();
    },
    [resetStateForTab]
  );

  // Status and action handlers
  const getStatusForCurrentTab = useCallback(() => {
    if (!sipocData) return "";

    const statusMap: Record<string, string> = {
      "1": sipocData.bd_stage,
      "2": sipocData.job_hie_stage,
      "3": sipocData.kpi_basket_stage,
      "4": sipocData.critical_matrix_stage,
      "5": sipocData.sla_stage,
    };

    return statusMap[value] || "";
  }, [value, sipocData]);

  const isStatusApprovedOrReverted = useCallback((status: string) => {
    return status === "Approved" || status === "Reverted";
  }, []);

  const getReasonForCurrentTab = useCallback(() => {
    if (!sipocData) return "";

    const tabDataMap: Record<string, any> = {
      "1": sipocData.business_desc_data,
      "2": sipocData.job_hierarchy_data,
      "3": sipocData.kpiBasketList?.[0],
      "4": sipocData.critical_matrices?.[0],
      "5": sipocData.bd_sla,
    };

    const tabData = tabDataMap[value];
    if (!tabData) return "";

    return tabData.reason_to_approve || tabData.reason_to_revert || "";
  }, [value, sipocData]);

  const handleAction = useCallback(
    async (action: "Approved" | "Reverted") => {
      const isApproveAction = action === "Approved";

      // Update UI state
      if (isApproveAction) {
        setRevertHidden(true);
      } else {
        setApproveHidden(true);
      }

      setTabTexts((prev) => ({
        ...prev,
        [value]: {
          ...prev[value],
          reasonText: prev[value].tempText,
        },
      }));

      setTabStates((prev) => ({
        ...prev,
        [value]: { showInput: true },
      }));

      const statusUpdater = isApproveAction
        ? setIsReasonApproved
        : setIsReasonReverted;
      statusUpdater((prev) => ({
        ...prev,
        [value]: true,
      }));

      setClickCount((prev) => prev + 1);

      if (clickCount + 1 === 2) {
        const pageIndex = parseInt(value) - 1;
        const requestBody = {
          action,
          id: sipocId,
          reason: tabTexts[value].tempText,
        };

        try {
          await apiService(
            `/management/business_description_status/${pageNames[pageIndex]}`,
            "PATCH",
            requestBody,
            undefined,
            instance as PublicClientApplication
          );

          await fetchBusinessDescription();
          setBackgroundColor(isApproveAction ? "#e6ffed" : "#ffebeb");

          if (isApproveAction) {
            setApproveHidden(false);
            setRevertHidden(true);
            setIsApprove(true);
          } else {
            setApproveHidden(true);
            setRevertHidden(false);
            setClickCount((prev) => prev + 1);

            if (clickCount + 1 === 3) {
              onClose();
              setClickCount(0);
            }
          }
        } catch (error) {
          setError(`Error ${action.toLowerCase()} the data.`);
        }
      }
    },
    [
      clickCount,
      value,
      sipocId,
      tabTexts,
      instance,
      fetchBusinessDescription,
      onClose,
    ]
  );

  // Effects
  useEffect(() => {
    if (!sipocId || !open) return;
    fetchBusinessDescription();
  }, [sipocId, open, fetchBusinessDescription]);

  useEffect(() => {
    if (value === "2" && open) {
      fetchJobHierarchyData();
    }
  }, [value, open, fetchJobHierarchyData]);

  if (!sipocData) {
    return null;
  }

  const currentStatus = getStatusForCurrentTab();
  const currentReason = getReasonForCurrentTab();
  const isLastTab = value === totalTabs.toString();
  const isFirstTab = value === "1";
  const showActionButtons =
    isManagementMatching && !isStatusApprovedOrReverted(currentStatus);

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Slide in={open} direction="left">
        <Box
          className="sipocmodald admin"
          sx={{
            width: 1000,
            padding: 4,
            position: "absolute",
            top: "0%",
            right: "1%",
            backgroundColor: "white",
            boxShadow: 24,
            borderRadius: 2,
            outline: 0,
            overflowY: "scroll",
          }}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              Business Description
            </Typography>
            <Box display="flex" gap={1}>
              <Typography variant="body1">Filter:</Typography>
              <Typography variant="body1">{functionL1}, </Typography>
              <Typography variant="body1">{functionL2}</Typography>
            </Box>

            <TabContext value={value}>
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  paddingTop: "15px",
                }}
              >
                <TabList onChange={handleChange} aria-label="modal tabs">
                  {pageNames.map((name, index) => (
                    <Tab
                      key={index + 1}
                      label={name}
                      value={(index + 1).toString()}
                    />
                  ))}
                </TabList>
              </Box>

              <TabPanel value="1">
                {loadingStates.tab1 ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <BusinessDescription
                    businessDescData={sipocData.business_desc_data}
                  />
                )}
              </TabPanel>

              <TabPanel value="2">
                <Typography variant="h6">Job Hierarchy</Typography>
                <DialogContent
                  sx={{
                    overflowY: "hidden",
                    padding: "0",
                    marginBottom: "30px",
                  }}
                >
                  {loadingStates.tab2 ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight="200px"
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <FilePreview
                      loading={loadingStates.tab2}
                      error={error}
                      imageSrc={imagePreview}
                      name={
                        sipocData?.job_hierarchy_data?.doc_path
                          ?.split("/")
                          .pop() || ""
                      }
                      fileSize={sipocData?.job_hierarchy_data?.file_size}
                    />
                  )}
                </DialogContent>
              </TabPanel>

              <TabPanel value="3">
                {loadingStates.tab3 ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <KPIBasket kpiBasketList={sipocData.kpiBasketList} />
                )}
              </TabPanel>

              <TabPanel value="4">
                {loadingStates.tab4 ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <CriticalMatrix
                    criticalMatrices={sipocData.critical_matrices}
                  />
                )}
              </TabPanel>
            </TabContext>
            {/* Status and Action Section */}

            {isStatusApprovedOrReverted(currentStatus) ? (
              <StatusDisplay status={currentStatus} reason={currentReason} />
            ) : (
              tabStates[value]?.showInput && (
                <ReasonInput
                  value={tabTexts[value].tempText}
                  onChange={(e) =>
                    setTabTexts((prev) => ({
                      ...prev,
                      [value]: { ...prev[value], tempText: e.target.value },
                    }))
                  }
                />
              )
            )}

            <ActionButtons
              className="modalfooterBD"
              isLastTab={isLastTab}
              showActionButtons={showActionButtons}
              isApprove={isApprove}
              approveHidden={approveHidden}
              revertHidden={revertHidden}
              onPrev={() => navigateTab("prev")}
              onNext={() => navigateTab("next")}
              onClose={onClose}
              onApprove={() => handleAction("Approved")}
              onRevert={() => handleAction("Reverted")}
              isFirstTab={isFirstTab}
            />
          </Box>
        </Box>
      </Slide>
    </Modal>
  );
};

// Sub-components for better organization
const StatusDisplay: React.FC<{ status: string; reason: string }> = ({
  status,
  reason,
}) => {
  const statusColor = status === "Reverted" ? "#C22F2F" : "#2D9E4D";
  const bgColor = status === "Reverted" ? "#ffebeb" : "#e6ffed";
  const borderColor = status === "Reverted" ? "red" : "green";

  return (
    <Box sx={{ flex: 1, pl: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          backgroundColor: bgColor,
          width: "fit-content",
          padding: "6px 12px",
          borderRadius: "26px",
          marginBottom: "25px",
        }}
      >
        <Box
          sx={{
            height: "8px",
            width: "8px",
            backgroundColor: statusColor,
            borderRadius: "50%",
          }}
        />
        <Typography
          sx={{
            fontSize: "16px",
            color: statusColor,
          }}
        >
          {status}
        </Typography>
      </Box>

      {reason && (
        <Typography
          className="putreasontext"
          sx={{
            color: "black",
            backgroundColor: bgColor,
            border: `1px solid ${borderColor}`,
            padding: 1,
            borderRadius: 1,
            marginTop: 1,
            marginBottom: "45px",
          }}
        >
          {reason}
        </Typography>
      )}
    </Box>
  );
};

const ReasonInput: React.FC<{
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}> = ({ value, onChange }) => (
  <Box sx={{ flex: 1, pl: 2 }}>
    <textarea
      className="putreasontext"
      placeholder="Enter the Reason"
      value={value}
      onChange={onChange}
    />
  </Box>
);

interface ActionButtonsProps {
  className?: string;
  isLastTab: boolean;
  isFirstTab?: boolean;
  showActionButtons: boolean;
  isApprove: boolean;
  approveHidden: boolean;
  revertHidden: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  onApprove: () => void;
  onRevert: () => void;
}

const isAdmin = localStorage.getItem("role") === "admin";

const ActionButtons: React.FC<ActionButtonsProps> = ({
  isLastTab,
  showActionButtons,
  isApprove,
  approveHidden,
  revertHidden,
  onPrev,
  onNext,
  onClose,
  onApprove,
  onRevert,
  isFirstTab,
}) => (
  <Box
    className="modalfooterBD"
    sx={{
      display: "flex",
      justifyContent: isFirstTab ? "flex-end" : "space-between",
      mt: 2,
    }}
  >
    {!isFirstTab && (
      <Button className="Footerbtn1" onClick={onPrev}>
        Previous
      </Button>
    )}

    <Box className="flex-item actionbtnsmodal">
      {!showActionButtons ? (
        <Button className="Footerbtn3" onClick={isLastTab ? onClose : onNext}>
          {isLastTab ? "Close" : "Next Tab"}
        </Button>
      ) : (
        <>
          {!revertHidden && (
            <Button className="revertbtn" onClick={onRevert}>
              Revert
            </Button>
          )}
          {!approveHidden && !isAdmin && !isApprove && (
            <Button className="approvebtn" onClick={onApprove}>
              Approve
            </Button>
          )}
          <Button className="Footerbtn3" onClick={isLastTab ? onClose : onNext}>
            {isLastTab ? "Close" : "Next Tab"}
          </Button>
        </>
      )}
    </Box>
  </Box>
);

export default SipocModalAdmin;
